import { useState } from "react";
import { User, Mail, MessageCircle, Edit3, Loader } from "lucide-react";
import { apiRequest, getToken } from "@ridecloud/shared";
import { devError } from "@ridecloud/shared";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Character limits for each field
  const charLimits = {
    name: 20,
    email: 25,
    subject: 50,
    message: 500,
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Apply character limits to all fields
    if (newValue.length > charLimits[name]) {
      return; // Don't update state if it would exceed limit
    }

    if (name === "name") {
      // Allow letters, numbers, and spaces only for name
      const valid = /^[A-Za-z0-9\s]*$/.test(newValue);
      if (!valid) return;
    }

    setFormData({ ...formData, [name]: newValue });

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    // Clear status when user modifies the form
    if (status) {
      setStatus("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid and ready for submission
  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.email.trim() &&
      formData.subject.trim() &&
      formData.message.trim() &&
      !Object.values(errors).some((error) => error) &&
      !isLoading
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setStatus("error");
      return;
    }

    setIsLoading(true);
    setStatus("");

    try {
      const response = await apiRequest(async (APIv1) =>
        APIv1.post("/public/submit_form", formData, {
          headers: {
            "Content-Type": "application/json",
          },
        })
      );

      setStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      devError("Error submitting contact form:", error);
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      id="contact us"
      className="relative py-20 bg-gradient-to-br from-white to-teal-50 overflow-hidden"
    >
      {/* Decorative Blobs */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-emerald-100 rounded-full blur-3xl opacity-40 animate-pulse" />
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-teal-100 rounded-full blur-3xl opacity-40 animate-pulse delay-200" />

      <div className="relative max-w-4xl mx-auto px-6 text-center space-y-12">
        {/* Heading */}
        <h2
          className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight opacity-0 animate-fade-in-up"
          style={{ animationDelay: "100ms" }}
        >
          Contact <span className="text-emerald-600">RideCloud</span>
        </h2>

        <p
          className="text-gray-600 opacity-0 animate-fade-in-up"
          style={{ animationDelay: "200ms" }}
        >
          Have a question or feedback? We'd love to hear from you. Fill out the
          form below.
        </p>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white p-10 rounded-3xl shadow-xl max-w-3xl mx-auto space-y-6 relative overflow-hidden"
        >
          {/* Modern background decoration */}
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-emerald-200 rounded-full blur-3xl opacity-40 animate-pulse"></div>
          <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-teal-200 rounded-full blur-3xl opacity-40 animate-pulse delay-200"></div>

          {/* Name & Email */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <div className="relative">
                <User className="absolute left-4 top-4 text-gray-400 w-5 h-5 transition-colors duration-300" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  className={`w-full pl-12 p-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                  maxLength={charLimits.name}
                  disabled={isLoading}
                />
              </div>
              <div className="flex justify-between items-center mt-1">
                {errors.name && (
                  <span className="text-xs text-red-500">{errors.name}</span>
                )}
                <span className="text-xs text-gray-500 ml-auto">
                  {formData.name.length}/{charLimits.name} characters
                </span>
              </div>
            </div>

            <div className="relative flex-1">
              <div className="relative">
                <Mail className="absolute left-4 top-4 text-gray-400 w-5 h-5 transition-colors duration-300" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  className={`w-full pl-12 p-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  required
                  maxLength={charLimits.email}
                  disabled={isLoading}
                />
              </div>
              <div className="flex justify-between items-center mt-1">
                {errors.email && (
                  <span className="text-xs text-red-500">{errors.email}</span>
                )}
                <span className="text-xs text-gray-500 ml-auto">
                  {formData.email.length}/{charLimits.email} characters
                </span>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div className="relative">
            <div className="relative">
              <Edit3 className="absolute left-4 top-4 text-gray-400 w-5 h-5 transition-colors duration-300" />
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Subject"
                className={`w-full pl-12 p-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition ${
                  errors.subject ? "border-red-500" : "border-gray-300"
                }`}
                required
                maxLength={charLimits.subject}
                disabled={isLoading}
              />
            </div>
            <div className="flex justify-between items-center mt-1">
              {errors.subject && (
                <span className="text-xs text-red-500">{errors.subject}</span>
              )}
              <span className="text-xs text-gray-500 ml-auto">
                {formData.subject.length}/{charLimits.subject} characters
              </span>
            </div>
          </div>

          {/* Message */}
          <div className="relative">
            <div className="relative">
              <MessageCircle className="absolute left-4 top-4 text-gray-400 w-5 h-5 transition-colors duration-300" />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                rows="5"
                className={`w-full pl-12 p-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition ${
                  errors.message ? "border-red-500" : "border-gray-300"
                }`}
                required
                maxLength={charLimits.message}
                disabled={isLoading}
              />
            </div>
            <div className="flex justify-between items-center mt-1">
              {errors.message && (
                <span className="text-xs text-red-500">{errors.message}</span>
              )}
              <span className="text-xs text-gray-500 ml-auto">
                {formData.message.length}/{charLimits.message} characters
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isFormValid()}
            className={`w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl shadow-lg transition transform flex items-center justify-center gap-2 ${
              !isFormValid()
                ? "opacity-50 cursor-not-allowed"
                : "hover:shadow-xl hover:scale-105"
            } ${isLoading ? "cursor-wait" : ""}`}
          >
            {isLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Message"
            )}
          </button>

          {/* Status Messages */}
          {status === "success" && (
            <p className="mt-4 text-green-600 font-medium text-center">
              Message sent successfully! We'll get back to you soon.
            </p>
          )}
          {status === "error" && (
            <p className="mt-4 text-red-600 font-medium text-center">
              Something went wrong. Please try again.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
